import React, { useState, useMemo } from 'react';
import { useMappedChangeRequest } from '@/seller/components/change-requests/ChangeRequestFilterableList';
import { useAsyncState, useDefaultScrollToId, useCanScroll, useAsyncStateWithIsLoading, useQueryState } from '@/utils/hooks';
import { getChangeRequestFilterData, searchChangeRequests } from '@/services/change-requests';
import ChangeRequestFilter, {
    getInitialChangeRequestFilterValue,
    ChangeRequestFilterValue,
} from '@/seller/components/change-requests/ChangeRequestFilter';
import ChangeRequestList from '@/seller/components/change-requests/ChangeRequestList';
import { BatchedChanges, ChangeRequest } from '@/types';
import { FilterButtons, LoadingIndicator, useIsFilterModified } from '@ygtechnologies/bygr-frontend-component-library';
import { orderBy } from 'lodash-es';
import { ButtonBar } from '@/components/Buttons';
import { ButtonPagination, useLocalPagination } from '@/components/ScrollPagination';

interface ChangeRequestMessagesListProps {
    projectId: number;
}
const ChangeRequestMessagesList: React.FC<ChangeRequestMessagesListProps> = ({ projectId }) => {
    const [filter, setFilter] = useQueryState<ChangeRequestFilterValue>(getInitialChangeRequestFilterValue());
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterData] = useAsyncState(() => getChangeRequestFilterData(projectId), [projectId], null);
    const defaultExpandedId = useDefaultScrollToId();
    const canScroll = useCanScroll(defaultExpandedId);
    const filterModified = useIsFilterModified(filter, getInitialChangeRequestFilterValue);

    const [changeRequests, setChangeRequests, reloadChangeRequest, isLoading] = useAsyncStateWithIsLoading(
        () =>
            searchChangeRequests({
                units: filter.units,
                groups: filter.groups,
                statuses: filter.statuses,
                participants: filter.participants,
                searchText: filter.searchText,
                projectId,
            }),
        [filter],
        null
    );

    const mappedChangeRequests = useMappedChangeRequest(changeRequests)?.filter((cr) => cr.isFollowing);

    const sortedChangeRequests = useMemo(() => {
        if (!mappedChangeRequests) {
            return null;
        }

        return orderBy(
            mappedChangeRequests,
            [(e) => e.lastMessageDate || new Date(1970, 0, 1), 'changeRequestId'],
            ['desc', 'asc']
        );
    }, [mappedChangeRequests]);

    const paginate = useLocalPagination(sortedChangeRequests, 30);

    const handleChange = (changes: BatchedChanges<ChangeRequest>) => {
        if (!changeRequests) {
            return;
        }
        setChangeRequests(changes.updateArray(changeRequests));
    };

    const handleReloadChangeRequests = () => {
        reloadChangeRequest();
    };

    return (
        <>
            <ButtonBar>
                <ButtonBar.Right>
                    <ChangeRequestFilter
                        isOpen={isFilterOpen}
                        onClose={() => setIsFilterOpen(false)}
                        filterData={filterData}
                        value={filter}
                        onFilterChange={setFilter}
                    />

                    <FilterButtons
                        searchText={filter.searchText}
                        onSearchTextChange={(v) => setFilter({ ...filter, searchText: v })}
                        isLoading={isLoading}
                        onButtonClick={() => setIsFilterOpen((v) => !v)}
                        isActive={filterModified}
                    />
                </ButtonBar.Right>
            </ButtonBar>

            {!isLoading ? (
                <ButtonPagination loader={paginate}>
                    {(pageData) => (
                        <ChangeRequestList
                            defaultExpandedId={canScroll ? defaultExpandedId : undefined}
                            changeRequests={pageData}
                            reloadChangeRequests={handleReloadChangeRequests}
                            onChange={handleChange}
                        />
                    )}
                </ButtonPagination>
            ) : (
                <LoadingIndicator />
            )}
        </>
    );
};

export default ChangeRequestMessagesList;
